import moment from "moment/moment"
import React, { Fragment, useContext, useEffect, useState } from "react"
import { AdminContext } from "~/contexts/AdminContext"
import Holidays from "date-holidays"
import { Menu, Transition } from "@headlessui/react"
import classNames from "~/utils/classNames"
import { CalendarIcon } from "@heroicons/react/24/solid"
import parseDateTime from "~/utils/parseDateTime"
import { DayPickerSingleDateController } from "react-dates"

interface DatePickerProps {
  selectedDate: string
  setSelectedDate: (value: moment.Moment) => void
  isPickerImageHidden?: boolean
  isStartDate?: string
  validFromDate?: moment.Moment
  validUntilDate?: moment.Moment
  isNoExpiration?: boolean
  error?: string
  className?: string
  initialMonth?: string
}

const CouponsDatePicker = (props: DatePickerProps) => {
  const { camp } = useContext(AdminContext)

  const holidays = new Holidays("US")
  const observedHolidays = holidays.getHolidays(moment().year())
  const [focused, setFocused] = useState(true)

  useEffect(() => {
    !focused && setFocused(true)
  }, [focused])

  return (
    <Menu as="div" className={classNames("relative block text-left mr-auto w-full md:w-auto", props.className)}>
      <div className={classNames("relative mb-1", props.className)}>
        <Menu.Button
          style={{ height: props.selectedDate === null ? 36 : undefined }}
          className={classNames(
            `justify-center whitespace-nowrap inline-flex items-center border rounded-md mt-1 md:mt-auto border-gray-300 shadow-sm px-4 py-2.5 bg-white font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2  focus:ring-offset-gray-100 open-sans noOutline w-52`,
            !!props.error?.length && "text-red-600 border-2 border-red-600",
            props.className,
          )}
        >
          {!props.isPickerImageHidden && (
            <CalendarIcon className="mr-3.5 -ml-1 mb-0.25 mt-0.25 h-5.5 w-5.5" aria-hidden="true" />
          )}
          {!props.isNoExpiration
            ? parseDateTime(props.selectedDate, camp.timezone, { asMoment: true })?.format("MMMM Do")
            : ""}
        </Menu.Button>
        {props.error?.length && (
          <span className="text-red-500 text-xssm absolute -bottom-6 left-1 z-0">{props.error}</span>
        )}
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="z-50 invoice-date-picker origin-top-right flex flex-col md:flex-row fixed lg:absolute left-1/2 lg:left-0 transform lg:transform-none -translate-x-1/2 mt-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div style={{ width: 318 }}>
            {focused && (
              // @ts-ignore
              <DayPickerSingleDateController
                noBorder={true}
                initialVisibleMonth={() => {
                  if (props.initialMonth) {
                    return parseDateTime(props.initialMonth, camp.timezone, { asMoment: true })
                  }
                  return !props.isNoExpiration
                    ? parseDateTime(props.selectedDate, camp.timezone, { asMoment: true })
                    : props.validFromDate?.isValid() ? props.validFromDate : moment()
                }}
                date={
                  !props.isNoExpiration
                    ? parseDateTime(props.selectedDate, camp.timezone, { asMoment: true })
                    : moment()
                }
                onDateChange={(date) => props.setSelectedDate(date?.startOf("day"))}
                focused={focused}
                onFocusChange={(e) => setFocused(e.focused)}
                numberOfMonths={1}
                hideKeyboardShortcutsPanel={true}
                isOutsideRange={(date) => {
                  if (props.validFromDate?.isValid() && date.isBefore(props.validFromDate, "day")) return true
                  return !!(props.validUntilDate?.isValid() && date.isAfter(props.validUntilDate, "day"))
                }}
                calendarInfoPosition="top"
              />
            )}
          </div>
          <div className="py-4.5 px-5 overflow-auto" style={{ maxHeight: 300 }}>
            <div className="mb-4.5 font-bold">Shortcuts</div>
            <div className="flex flex-col gap-3">
              {observedHolidays.map((holiday) => {
                return (
                  <div
                    className="text-blue-500 font-semibold whitespace-nowrap cursor-pointer"
                    onClick={() => {
                      const sanitizedDate = holiday.date.replace(" ", "T")
                      const parsedHolidayDate = parseDateTime(sanitizedDate, camp.timezone, { asMoment: true })
                      if (parsedHolidayDate && parsedHolidayDate.isValid()) {
                        props.setSelectedDate(parsedHolidayDate)
                        setFocused(false)
                      } else {
                        console.error("Invalid holiday date:", holiday.date)
                      }
                    }}
                  >
                    {holiday.name}
                  </div>
                )
              })}
            </div>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export default CouponsDatePicker
